import React from 'react';
import { RefreshCw, Sparkles } from 'lucide-react';
import clsx from 'clsx';
import GenericModal from '@components/common/modals/GenericModal';
import Button from '@components/common/Button';
import { Textarea } from '@components/common';
import styles from './CreateWidgetModal.module.css';

interface CreateWidgetModalProps {
  isOpen: boolean;
  onClose: () => void;
  nlQuery: string;
  setNlQuery: (query: string) => void;
  isGenerating: boolean;
  onCreateWidget: () => Promise<void>;
}

const CreateWidgetModal: React.FC<CreateWidgetModalProps> = ({
  isOpen,
  onClose,
  nlQuery,
  setNlQuery,
  isGenerating,
  onCreateWidget
}) => {
  const footer = (
    <>
      <Button variant="ghost" onClick={onClose} disabled={isGenerating}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={onCreateWidget}
        disabled={!nlQuery.trim() || isGenerating}
        dataTestId="create-widget-submit"
      >
        {isGenerating ? (
          <>
            <RefreshCw className={clsx(styles.icon, styles.animateSpin)} size={16} />
            Creating widget...
          </>
        ) : (
          <>
            <Sparkles size={16} className={styles.icon} />
            Create Widget
          </>
        )}
      </Button>
    </>
  );

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <>
          <Sparkles size={20} className={styles.icon} />
          Create AI-Powered Widget
        </>
      }
      footer={footer}
      size="md"
      disableBackdropClick={isGenerating}
      dataTestId="create-widget-modal"
    >
      <div>
        <p className={styles.modalDescription}>
          Describe what you want to see in plain English and we'll create the perfect widget for you.
        </p>

        <div className={styles.formGroup}>
          <Textarea
            label="What would you like to analyze?"
            rows={4}
            placeholder="e.g., Show me assessment completion rates by supplier tier over the last 6 months"
            value={nlQuery}
            onChange={(e) => setNlQuery(e.target.value)}
            disabled={isGenerating}
            dataTestId="nl-query"
          />
        </div>

        <div className={styles.exampleQueries}>
          <p>
            <strong>Try these examples:</strong>
          </p>
          <div className={styles.examplesList}>
            <button
              className={styles.exampleButton}
              onClick={() => setNlQuery('Show me evidence review performance by assessor')}
              disabled={isGenerating}
              data-test-id="example-query-0"
            >
              Evidence review performance by assessor
            </button>
            <button
              className={styles.exampleButton}
              onClick={() => setNlQuery('Which suppliers have the most high-priority gaps?')}
              disabled={isGenerating}
              data-test-id="example-query-1"
            >
              Suppliers with most high-priority gaps
            </button>
            <button
              className={styles.exampleButton}
              onClick={() => setNlQuery('Track assessment completion trends over time')}
              disabled={isGenerating}
              data-test-id="example-query-2"
            >
              Assessment completion trends
            </button>
          </div>
        </div>
      </div>
    </GenericModal>
  );
};

export default CreateWidgetModal;
